// Ported from apps/www/src/utils/authRouting.ts (the deprecated codebase auth.offsetmind.com used
// to run from). This site is the single central-auth host -- every other site redirects here to
// sign in, then back to itself via redirect_uri.
const API_BASE_BY_UI_HOST = {
    "ui.dev.offsetmind.com": "https://api.dev.offsetmind.com/api/v1",
    "ui.nonprod.offsetmind.com": "https://api.nonprod.offsetmind.com/api/v1",
    "mgmt.offsetmind.com": "https://mgmt.api.offsetmind.com/api/v1",
    "www.offsetmind.com": "https://api.offsetmind.com/api/v1",
};
const POST_LOGIN_FALLBACK_PATH = "/apps";
export const isOffsetmindHostname = (hostname) => {
    const host = String(hostname || "").trim().toLowerCase();
    return host === "offsetmind.com" || host.endsWith(".offsetmind.com");
};
// chromiumapp.org is Chrome's own pseudo-domain for extension OAuth redirects
// (chrome.identity.launchWebAuthFlow / getRedirectURL()) -- it's never a real
// DNS host, Chrome intercepts navigation to it internally before any request
// leaves the browser. Allowing it here lets the Offsetmind browser extension
// use this same sign-in page instead of Cognito's default hosted UI, without
// loosening anything for actual web redirects.
export const isChromeExtensionRedirectHostname = (hostname) => {
    const host = String(hostname || "").trim().toLowerCase();
    return host.endsWith(".chromiumapp.org");
};
const isAuthPath = (pathname) => {
    const normalized = pathname.replace(/\/+$/, "").toLowerCase();
    return normalized === "/auth" || normalized.startsWith("/auth/");
};
const normalizePostAuthTarget = (url) => {
    if (!isAuthPath(url.pathname)) {
        return url.toString();
    }
    const normalized = new URL(POST_LOGIN_FALLBACK_PATH, url.origin);
    const inviteCode = url.searchParams.get("invite_code") || url.searchParams.get("inviteCode");
    const tenantId = url.searchParams.get("tenant_id") ||
        url.searchParams.get("tenantId") ||
        url.searchParams.get("tenant");
    if (inviteCode) {
        normalized.searchParams.set("invite_code", inviteCode);
    }
    if (tenantId) {
        normalized.searchParams.set("tenant_id", tenantId);
    }
    return normalized.toString();
};
export const isAllowedAuthRedirect = (value, baseOrigin) => {
    const candidate = String(value || "").trim();
    if (!candidate) {
        return null;
    }
    try {
        const fallbackOrigin = baseOrigin ||
            (typeof window !== "undefined" ? window.location.origin : "https://www.offsetmind.com");
        const resolved = new URL(candidate, fallbackOrigin);
        if (resolved.protocol !== "https:" && resolved.hostname !== "localhost") {
            return null;
        }
        if (resolved.hostname === "localhost" || isOffsetmindHostname(resolved.hostname)) {
            return normalizePostAuthTarget(resolved);
        }
        // Extension redirects aren't an app route (no /apps fallback, no
        // invite_code/tenant_id rewriting makes sense here) -- pass the target
        // through as-is.
        if (isChromeExtensionRedirectHostname(resolved.hostname)) {
            return resolved.toString();
        }
    }
    catch {
        return null;
    }
    return null;
};
export const readRedirectTargetFromSearch = (search, baseOrigin) => {
    const params = new URLSearchParams(search ?? (typeof window !== "undefined" ? window.location.search : ""));
    return isAllowedAuthRedirect(params.get("redirect_uri"), baseOrigin);
};
export const resolveTargetApiBaseUrl = (fallbackApiBaseUrl, search) => {
    if (typeof window === "undefined") {
        return fallbackApiBaseUrl;
    }
    // A pending upstream OIDC broker request (Matrix/Element X-style federated sign-in, see
    // adapter.tsx) is a self-contained flow that never wants another site's redirect_uri --
    // and reading it here previously meant a stale `coremind:post-auth-redirect` left over from
    // an unrelated earlier visit (e.g. a camera-ui pairing page on a dev/nonprod host) could
    // silently reroute this env's own auth API calls to that other host, even for real prod
    // users. Bail out before touching storage in that case.
    const searchParams = new URLSearchParams(search ?? (typeof window !== "undefined" ? window.location.search : ""));
    if (searchParams.get("oidc_request")) {
        return fallbackApiBaseUrl;
    }
    const stashKey = "coremind:post-auth-redirect";
    const fromUrl = readRedirectTargetFromSearch(search);
    const fromSession = isAllowedAuthRedirect(window.sessionStorage.getItem(stashKey));
    const fromLocal = isAllowedAuthRedirect(window.localStorage.getItem(stashKey));
    const redirectTarget = fromUrl || fromSession || fromLocal;
    // Single-use: whichever stash contributed the value must not leak into the next,
    // unrelated navigation on this host once it's been consumed here.
    if (fromSession)
        window.sessionStorage.removeItem(stashKey);
    if (fromLocal)
        window.localStorage.removeItem(stashKey);
    if (!redirectTarget) {
        return fallbackApiBaseUrl;
    }
    try {
        const targetHost = new URL(redirectTarget).hostname.toLowerCase();
        return API_BASE_BY_UI_HOST[targetHost] || fallbackApiBaseUrl;
    }
    catch {
        return fallbackApiBaseUrl;
    }
};
